import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define the current version of the slice
const SLICE_VERSION = '1.3';

// 🔹 Exported initial state for use in version-based merging
export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [
    { id: 'Appointments', name: 'My Appointments' },
  ],
  dynamicTabs: [],
  activeTab: 0,

  fetchedWarehouseIds: [],
  warehousesByLocationId: {},

  // 🔹 NEW — Persisted User Selections
  lastSelectedLocation: null,        // { id, locationRef, locationName }
  lastSelectedWarehouse: null,       // { id, locationRef, locationName }
  lastSelectedDate: null,            // YYYY-MM-DD

  // 🔹 NEW — Separate selections for Calendar Settings tab
  lastSettingsLocation: null,        // { id, locationRef, locationName }
  lastSettingsWarehouse: null,       // { id, locationRef, locationName }
};

// 🔹 Create Appointments slice
const AppointmentsSlice = createSlice({
  name: 'Appointments',
  initialState,
  reducers: {
    addTab: (state, action) => {
      let tab = action.payload;

      if (!tab) {
        const dynamicId =
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`;

        tab = {
          id: `dyn-${dynamicId}`,
          name: `New Appointment ${state.dynamicTabs.length + 1}`,
          contentType: "newAppointment",
          isDynamic: true,
          createdAt: Date.now(),
        };
      }

      // ✅ Dedupe: Prevent duplicate tabs
      if (tab?.key) {
        const existingIdx = state.dynamicTabs.findIndex(
          (t) => t.key === tab.key && t.contentType === tab.contentType
        );

        if (existingIdx !== -1) {
          state.activeTab = state.defaultTabs.length + existingIdx;
          return;
        }
      }

      pushDynamicTab(state, tab, state.defaultTabs.length);
    },

    updateAppointmentTab: (state, action) => {
      const { appointmentId, appointmentData } = action.payload || {};
      if (!appointmentId || !appointmentData) return;

      state.dynamicTabs.forEach((tab) => {
        const matchesId =
          tab?.appointmentData?.appointmentId === appointmentId ||
          tab?.id === `view-appointment-${appointmentId}` ||
          tab?.id === `edit-appointment-${appointmentId}`;
        if (!matchesId) return;
        tab.appointmentData = appointmentData;
      });
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;

      // Prevent deleting default tabs
      if (tabIndex < state.defaultTabs.length) return;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);

      state.activeTab = Math.min(
        state.activeTab,
        state.defaultTabs.length + state.dynamicTabs.length - 1
      );
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.defaultTabs.length + state.dynamicTabs.length) {
        state.activeTab = index;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
    },

    // ---------- EXISTING STATES ----------
    addFetchedWarehouseId: (state, action) => {
      const id = action.payload;
      if (!state.fetchedWarehouseIds.includes(id)) {
        state.fetchedWarehouseIds.push(id);
      }
    },

    setWarehousesForLocation: (state, action) => {
      const { locationId, warehouses } = action.payload;
      state.warehousesByLocationId[locationId] = warehouses;
    },

    // ---------- NEW REDUCERS FOR PERSISTING SELECTIONS ----------
    setLastSelectedLocation: (state, action) => {
      state.lastSelectedLocation = action.payload;
    },

    setLastSelectedWarehouse: (state, action) => {
      state.lastSelectedWarehouse = action.payload;
    },

    setLastSelectedDate: (state, action) => {
      state.lastSelectedDate = action.payload;
    },

    setLastSettingsLocation: (state, action) => {
      state.lastSettingsLocation = action.payload;
    },

    setLastSettingsWarehouse: (state, action) => {
      state.lastSettingsWarehouse = action.payload;
    },
  },
});

// 🔹 Export actions and reducer
export const {
  addTab,
  updateAppointmentTab,
  removeTab,
  setActiveTab,
  resetTabs,
  addFetchedWarehouseId,
  setWarehousesForLocation,

  // NEW exports
  setLastSelectedLocation,
  setLastSelectedWarehouse,
  setLastSelectedDate,

  setLastSettingsLocation,
  setLastSettingsWarehouse,
} = AppointmentsSlice.actions;

export default AppointmentsSlice.reducer;
